<template>
    <div class="forum-thread panel">
        <div
            class="panel-body"
            v-show="thread">

            <div class="flex">
                <div class="thread-title flex-full">
                    <h1 v-if="!editingTitle && thread">
                        {{ thread.title }}
                        <button
                            title="Edit title"
                            @click="editTitle()"
                            v-if="canEditThread">
                            <i class="fa fa-pencil"></i>
                        </button>
                        <button
                            :disabled="editingSticky_saving"
                            :class="{ 'accent-sticky': thread.sticky }"
                            :title="!thread.sticky ? 'Stick thread' : 'Unstick thread'"
                            @click="toggleSticky()"
                            v-if="canStickThread">
                            <i class="fa fa-thumb-tack"></i>
                        </button>
                    </h1>
                    <div v-if="editingTitle">
                        <div class="input-wrapper">
                            <input
                                class="form-control"
                                type="text"
                                ref="titleInput"
                                :disabled="editingTitle_saving"
                                @keyup.enter="saveTitle()"
                                v-model="editingTitle_value">
                            <button
                                title="Save title"
                                :disabled="editingTitle_saving"
                                @click="saveTitle()">
                                <i class="fa fa-save"></i>
                            </button>
                        </div>
                    </div>
                </div>
                <div class="action-bar flex-none">
                    <router-link :to="searchUrl">
                        <i class="fa fa-search"></i>
                    </router-link>
                </div>
            </div>

            <div class="thread-category">
                <div v-if="!editingCategory && threadCategory">
                    <router-link :to="homeUrl">Home</router-link>
                    <span class="breadcrumb-divider">/</span>
                    <router-link :to="threadCategoryUrl">
                        {{ threadCategory.label }}
                    </router-link>
                    <button
                        title="Edit category"
                        @click="editCategory()"
                        v-if="canEditThread">
                        <i class="fa fa-pencil"></i>
                    </button>
                </div>
                <div v-if="editingCategory">
                    <DropdownSelect
                        :disabled="editingCategory_saving"
                        :options="categoryOptions"
                        v-model="editingCategory_value">
                    </DropdownSelect>
                    <button
                        title="Save category"
                        :disabled="editingCategory_saving"
                        @click="saveCategory()">
                        <i class="fa fa-save"></i>
                    </button>
                </div>
            </div>

            <div class="discussion">
                <div class="posts-column">

                    <div v-show="showCurrentPostWindow">
                        <LoaderBefore v-show="showLoaderBefore">
                        </LoaderBefore>

                        <ForumPostList
                            ref="postList"
                            :thread="threadWrapper"
                            :postStore="postStore"
                            :postWindow="currentPostWindow"
                            :currentUser="currentUser"
                            @create-post="createPost($event)"
                            @update-post="updatePost($event)"
                            @like-post="likePost($event)"
                            @unlike-post="unlikePost($event)"
                            @delete-post="deletePost($event)"
                            @update-thread="updateThread($event)"
                            @solve-thread="solveThread($event)"
                            @unsolve-thread="unsolveThread($event)"
                            @go-post="goToPost($event)"
                            @view-post="viewPost($event)"
                            v-if="!loading_initial">
                        </ForumPostList>

                        <LoaderAfter v-show="showLoaderAfter">
                        </LoaderAfter>
                    </div>

                    <Loader v-show="showLoader">
                    </Loader>
                </div>

                <div class="pre-timeline-spacer flex-full"></div>

                <ForumThreadTimeline
                    sticky="true"
                    stayInParent="true"
                    ref="timeline"
                    :stickyPadding="timeline_screenTopPad"
                    :value="timelineValue"
                    :startDate="threadWrapper.firstDate"
                    :lastDate="threadWrapper.lastDate"
                    :posts="timelinePosts"
                    @input="setTimelineValue($event)"
                    @thumbgrab="onTimelineGrab()"
                    @thumbrelease="onTimelineRelease()">
                </ForumThreadTimeline>
            </div>

            <div class="thread-reply">
                <ForumPost
                    ref="threadReply"
                    class="thread-reply"
                    :post="threadReply"
                    :isEditing="true"
                    @cancel="cancelThreadReply()"
                    @save="saveThreadReply($event)"
                    v-if="threadReply">
                    <template slot="saveEditorLabel">
                        Post reply
                    </template>
                </ForumPost>
                <div class="post-reply-spacer"></div>
            </div>

            <div class="action-btns">
                <button
                    :class="threadReplyBtnClasses"
                    @click="initThreadReply()">
                    <i class="fa fa-reply"></i>
                    Reply
                </button>
                <span class="link-share-wrapper">
                    <button
                        @click="toggleLinkShare()"
                        class="btn btn-default"
                        :class="{ 'accent-hover': linkShareShowing }">
                        <i class="fa fa-link"></i>
                        Share
                    </button>
                    <ForumShare
                        ref="linkShare"
                        :value="threadUrl"
                        @close="closeLinkShare()"
                        v-show="linkShareShowing">
                    </ForumShare>
                </span>
            </div>

            <div v-if="showRelatedThreads">
                <h2>Related Threads</h2>

                <ForumThreadList :threads="relatedThreads">
                </ForumThreadList>

                <div class="see-more-link">
                    <router-link :to="seeMoreUrl">
                        View All
                    </router-link>
                </div>
            </div>
        </div>

        <div
            class="panel-body"
            v-if="!thread && !threadNotFound">
            <Loader></Loader>
        </div>

        <div
            class="panel-body"
            v-if="threadNotFound">
            <ForumThreadNotFound></ForumThreadNotFound>
        </div>
    </div>
</template>

<script>
    // ForumThread: handles all rendering and actions surrounding a forum
    // thread. Renders an auto-loading list of posts. Allows the user to slide
    // to any section of the thread with the thread timeline. Allows admins to
    // edit the thread or posts within it. Allows users to create replies.
    // Shows related threads beneath the discussion.

    import toastr from 'toastr';

    import DropdownSelect from './reusable/forum/dropdown-select';
    import ForumPost from './reusable/forum/post';
    import ForumPostList from './reusable/forum/post-list';
    import ForumService from '../services/forum';
    import ForumShare from './reusable/forum/share';
    import ForumThreadList from './reusable/forum/thread-list';
    import ForumThreadNotFound from './reusable/forum/thread-not-found';
    import ForumThreadTimeline from './reusable/forum/thread-timeline';
    import ForumUtils from '../utils/forum';
    import Loader from './reusable/forum/loader';
    import LoaderAfter from './reusable/forum/loader-after';
    import LoaderBefore from './reusable/forum/loader-before';


    function getScrollPos() {
        return $(window).scrollTop();
    }

    function getTopPos(elem) {
        return $(elem).offset().top;
    }

    function getBottomPos(elem) {
        const $elem = $(elem);
        return $elem.offset().top + $elem.outerHeight();
    }

    function getHeight(elem) {
        return $(elem).outerHeight();
    }

    function getWindowHeight() {
        return $(window).height();
    }

    function throttleToAnimFrame(fn) {
        let sched = false;
        return function() {
            if (!sched) {
                requestAnimationFrame(function() {
                    sched = false;
                    fn();
                });
                sched = true;
            }
        }
    }


    export default {
        components: {
            DropdownSelect,
            ForumPost,
            ForumPostList,
            ForumShare,
            ForumThreadList,
            ForumThreadNotFound,
            ForumThreadTimeline,
            Loader,
            LoaderAfter,
            LoaderBefore,
        },

        data() {
            const threadWrapper = new ForumUtils.Thread();
            const currentPostWindow = new ForumUtils.PostWindow(
                threadWrapper.postStore,
                null, // offset
                0,    // length
            );

            return {
                // There are a whole whack of resources that come together to
                // power the forum thread. Some are essential and must be loaded
                // before other actions can be started, whereas others can wait
                // until after the essential resources have been dealt with.
                // This promise encapsulates the loading of the essential
                // resources.
                initPromise: null,

                // Views aren't recorded until a couple seconds after the thread
                // has loaded. This allows us to cancel the action if the thread
                // is unmounted before the threadhold is reached.
                viewTimeout: null,

                // The thread wrapper is the utility thread class that wraps the
                // thread actions and state.
                // TODO: come up with a better name for either the utility class
                // or this instance variable.
                threadWrapper,

                // The current window of posts that is within view. See the
                // forum utility docs for more info on post windows.
                currentPostWindow,

                threadNotFound: false,

                // The following flag is only used while loading the first posts
                // of the thread. This allows us to display a loading message
                // specifically on startup.
                loading_initial: true,

                // TODO: remove this, it's no longer used.
                //
                // When using the timeline to go to a specific post, we want to
                // to automatically scroll to that post once it loads; we store
                // the post index here. If the user begins scrolling around
                // before the post loads, we can clear this variable and cancel
                // the auto-scroll.
                loading_scrollToPostOnLoad: null,

                // The timelineValue is a value between 0 and 1 that represents
                // where the user is in the discussion. Each post is given an
                // equal slice of the timeline, regardless of it's content size.
                timelineValue: 0,

                // The current post specified in the timeline (ie. the "20" in
                // "20 / 50") is determined by looking at which post the scroll
                // position is currently within. However, the scroll position
                // is the pageY value at the very top of the viewport, which
                // probably isn't where the user is looking. So to make the
                // current post value just a little more useful, we can offset
                // the scroll position a bit to where the user may actually be
                // reading.
                timeline_scrollOffset: -40 + 1,

                // As the user scrolls down the page, the timeline element will
                // follow alongside the discussion. This value determines the
                // padding between the timeline and the top of the viewport.
                timeline_screenTopPad: 40,

                // Track whether the "thumb" of the timeline slider is held down
                // so that we can enable/disable other behavior, such as
                // updating the timeline value based on the page scroll.
                timeline_thumbHeld: false,

                // List of related threads to show beneath the discussion.
                relatedThreads: [],

                // Stores the content when editing a thread.
                editingTitle: false,
                editingTitle_value: null,
                editingTitle_saving: false,
                editingCategory: false,
                editingCategory_value: null,
                editingCategory_saving: false,
                editingSticky_saving: false,

                // Stores the content when creating a new thread reply (post
                // replies are handled by ForumPostList).
                threadReply: null,

                // Whether the ForumShare for the entire thread is rendering or
                // not.
                linkShareShowing: false,

                // Throttled versions of the `onScroll` and `onResize` methods.
                throttledOnScroll: null,
                throttledOnResize: null,
            };
        },

        created() {
            this.initPromise = (async () => {
                this.loading_initial = true;

                const threadId = parseInt(this.$route.params.threadId);
                this.threadWrapper.init(
                    threadId,
                    this.cachedUserId,
                    this.cachedUserRoles,
                );

                const loadCurrentUser = async () => {
                    await this.$store.dispatch('ensureCurrentUser');
                };
                const loadThread = async () => {
                    try {
                        await this.threadWrapper.load();
                    } catch (err) {
                        // TODO: handle 404 specifically
                        this.threadNotFound = true;
                        throw err;
                    }
                };

                const loadInitialPosts = async () => {
                    const postId = this.$route.params.postId;
                    // const index = postId ? parseInt(postId) - 1 : 0;
                    const index = 0;
                    await this.postStore.loadPageAt(index);
                    this.timelineValue = this.indexToTimelineValue(index);
                };
                await Promise.all([
                    loadCurrentUser(),
                    loadThread(),
                    loadInitialPosts(),
                ]);

     

            
                this.loading_initial = false;
            })();

            this.initPromise.then(async () => {
                // We need categories if the user is able to edit the thread.
                // We load this after the more important stuff.
                if (this.canEditThread) {
                    await this.$store.dispatch('ensureForumCategories');
                }
            });

            this.initPromise.then(async () => {
                // Related threads are considered secondary, so load them after
                // the more important stuff.
                // Currently, related threads are just threads in the same
                // category.
                const res = await ForumService.getThreadsAndCount({
                    categoryId: this.threadCategory.id,
                    limit: 6,
                });

                // Make sure not to include this thread.
                const threadId = this.thread.id;
                const threads = res.threads.filter(t => t.id !== threadId);
                threads.length = Math.min(5, threads.length);

                this.relatedThreads = threads;
            });
        },

        async mounted() {
            await this.initPromise;

            this.updateWindowTitle();

            // Scroll to and highlight the post defined by the query param
            // (timeline value was set within initPromise).
            if (this.$route.params.postId) {

                // Scroll to
                this.setScrollFromTimelineValue(this.timelineValue);

                // Highlight
                const postId = parseInt(this.$route.params.postId);
                this.$refs.postList.flashPost(postId);

            } else {
                // We're not loading a specific post, so just make sure the
                // timeline value is accurate, in case the user has scrolled
                // already.
                this.setTimelineValueFromCurrentScroll();
            }

            // Auto-scroll to new posts when the current post list changes
            this.$watch('showCurrentPostWindow', function(newValue, oldValue) {
                if (newValue && !oldValue) {
                    const tv = this.timelineValue;
                    this.$nextTick(() => {
                        this.setScrollFromTimelineValue(tv);
                    });
                }
            });

            // Set up event handlers
            this.throttledOnScroll = throttleToAnimFrame(this.onScroll);
            this.throttledOnResize = throttleToAnimFrame(this.onResize);
            window.addEventListener('scroll', this.throttledOnScroll);
            window.addEventListener('resize', this.throttledOnResize);
            window.addEventListener('keyup', this.onKeyUp);

            // Without this, the timeline component shoots down the page after
            // the initial load.
            // TODO: figure out how to remove me
            this.$refs.timeline.updateStickyMode();

            // TODO: remove me (make sure clicking on timeline slider doesn't
            // jump to left side of screen)
            this.$refs.timeline.updateRootRightPosition();

            // All of the initial content has now been loaded; give the user a
            // few seconds to look at the content, then count the thread as
            // viewed.
            this.viewTimeout = setTimeout(async () => {
                await this.threadWrapper.createView();
            }, 2000);
        },

        beforeDestroy() {
            clearTimeout(this.viewTimeout);
            window.removeEventListener('scroll', this.throttledOnScroll);
            window.removeEventListener('resize', this.throttledOnResize);
            window.removeEventListener('keyup', this.onKeyUp);
        },

        methods: {
            onScroll() {
                // If enabled, update the timeline value based on the new scroll
                // position. This may be disabled if the user is using the
                // timeline slider.
                if (this.timelineValue_syncToScroll) {
                    this.setTimelineValueFromCurrentScroll();
                }

                // Automatically load posts when the user scrolls past the
                // beginning or end of the current post window.
                this.autoLoadPostsBeforeCurrentWindow();
                this.autoLoadPostsAfterCurrentWindow();
            },

            onResize() {
                // Resizing the window may change the height of the posts, which
                // may change which post the scroll position lies within. So
                // make sure the timeline value is still up to date.
                if (this.timelineValue_syncToScroll) {
                    this.setTimelineValueFromCurrentScroll();
                }

                // If the window is made larger, we may need more posts to fill
                // the empty space.
                this.autoLoadPostsBeforeCurrentWindow();
                this.autoLoadPostsAfterCurrentWindow();
            },

            onKeyUp(ev) {
                if (ev.key === 'Escape') {
                    if (this.editingTitle && !this.editingTitle_saving) {
                        this.editingTitle = false;
                        return;
                    }
                    if (this.editingCategory && !this.editingCategory.saving) {
                        this.editingCategory = false;
                        return;
                    }
                }
            },

            onTimelineGrab() {
                this.timeline_thumbHeld = true;
            },

            async onTimelineRelease() {
                this.timeline_thumbHeld = false;

                // Move the post window
                const tv = this.timelineValue;
                const pi = this.timelineValueToIndex(tv);
                const postWindow = this.postStore.getLoadedWindowAt(pi);
                this.currentPostWindow = postWindow;

                // Move the scroll back to the top if the window needs to load
                if (postWindow.length === 0 || !postWindow.isLoaded()) {
                    this.setScrollFromTimelineValue(0);
                }
                await this.postStore.loadPageAt(pi);

                // Scroll to the post
                await this.$nextTick();
                this.setScrollFromTimelineValue(tv);
            },

            async goToPost(postId) {

                // Update the timeline value
                const postIndex = postId - 1;
                const tv = this.indexToTimelineValue(postIndex);
                this.setTimelineValue(tv);

                // Update the current post window
                const postWindow = this.postStore.getLoadedWindowAt(postIndex);
                this.currentPostWindow = postWindow;

                // Move the scroll back to the top if the window needs to load
                if (postWindow.length === 0 || !postWindow.isLoaded()) {
                    this.setScrollFromTimelineValue(0);
                }
                await this.postStore.loadPageAt(postIndex);

                // Scroll to the post and flash it
                await this.$nextTick();
                this.setScrollFromTimelineValue(tv);
                this.$refs.postList.flashPost(postId);
            },

            async viewPost(postId) {
                await ForumService.createPostView(this.thread.id, postId);
            },

            setTimelineValue(value) {
                this.timelineValue = value;
            },

            setTimelineValueFromCurrentScroll() {
                const pos = getScrollPos();
                this.setTimelineValueFromScroll(pos);
            },

            setTimelineValueFromScroll(scrollPos) {
                scrollPos -= this.timeline_scrollOffset;
                this.timelineValue = (
                    this.$refs.postList.pageYToTimelineValue(scrollPos)
                );
            },

            setScrollFromTimelineValue(timelineValue) {
                const postList = this.$refs.postList;
                const timeline = this.$refs.timeline;
                if (!postList || !timeline) {
                    return;
                }

                // TODO: remove +1 below, but make sure timeline index is
                // accurate when loading specific post on page load.
                let scrollPos = postList.timelineValueToPageY(timelineValue);
                scrollPos += this.timeline_scrollOffset + 1;

                // Cap scroll position so that the timeline controls can't
                // scroll themselves off screen.
                const postListBottomPos = getBottomPos(postList.$el);
                const timelineHeight = getHeight(timeline.$el);
                const maxScroll = (
                    postListBottomPos -
                    timelineHeight -
                    this.timeline_screenTopPad
                );
                scrollPos = Math.min(scrollPos, maxScroll);

                const leftScrollPos = $(window).scrollLeft();
                window.scrollTo(leftScrollPos, scrollPos);
            },

            timelineValueToIndex(timelineValue) {
                if (this.postStore.length === null ||
                    this.postStore.length === 0) {
                    return null;
                }
                timelineValue = Math.max(0, Math.min(1, timelineValue));
                return Math.min(
                    Math.floor(timelineValue * this.postStore.length),
                    this.postStore.length - 1,
                );
            },

            indexToTimelineValue(index) {
                if (this.postStore.length === null ||
                    this.postStore.length === 0) {
                    return null;
                }
                return Math.max(0, Math.min(1, index / this.postStore.length));
            },

            async autoLoadPostsBeforeCurrentWindow() {
                // If the current window is at the beginning, there is nothing
                // to load.
                if (!this.currentPostWindow.hasPostsBefore()) {
                    return;
                }

                // If we aren't near the top of the current window, we don't
                // have to load anything.
                const floatingTv = (() => {
                    const postList = this.$refs.postList;
                    const scrollPos = getScrollPos();
                    const tvAtTop = postList.pageYToTimelineValue(scrollPos);

                    const buffer = 1; // Must have 1 post loaded above screen
                    const bufferTv = buffer / this.postStore.length;
                    return tvAtTop - bufferTv;
                })();
                const tvMin = (() => {
                    const index = this.currentPostWindow.firstPostIndex;
                    return this.indexToTimelineValue(index);
                })();
                if (floatingTv > tvMin) {
                    return;
                }

                // Load the next page.
                const index = this.currentPostWindow.firstPostIndex;
                await this.postStore.loadPageBefore(index);
            },

            async autoLoadPostsAfterCurrentWindow() {
                // If the current window is at the end, there is nothing to
                // load.
                if (!this.currentPostWindow.hasPostsAfter()) {
                    return;
                }

                // If we aren't near the bottom of the current window, we don't
                // have to load anything.
                const floatingTv = (() => {
                    const postList = this.$refs.postList;
                    const scrollPos = getScrollPos();
                    const windowHeight = getWindowHeight();
                    const bottomPos = scrollPos + windowHeight - 1;
                    const tvAtBottom = postList.pageYToTimelineValue(bottomPos);

                    const buffer = 3; // Must have 3 posts loaded below screen
                    const bufferTv = buffer / this.postStore.length;
                    return tvAtBottom + bufferTv;
                })();
                const tvMax = (() => {
                    const index = this.currentPostWindow.lastPostIndex;
                    return this.indexToTimelineValue(index + 1);
                })();
                if (floatingTv < tvMax) {
                    return;
                }

                // Load the next page.
                const index = this.currentPostWindow.lastPostIndex;
                await this.postStore.loadPageAfter(index);
            },

            editTitle() {
                if (this.editingTitle) {
                    return;
                }
                this.editingTitle_value = this.thread.title;
                this.editingTitle_saving = false;
                this.editingTitle = true;
                this.$nextTick(() => {
                    if (this.$refs.titleInput) {
                        this.$refs.titleInput.focus();
                    }
                });
            },

            editCategory() {
                if (this.editingCategory) {
                    return;
                }
                this.editingCategory_value = this.threadCategory.id;
                this.editingCategory_saving = false;
                this.editingCategory = true;
            },

            async saveTitle() {
                if (this.editingTitle_saving) {
                    return;
                }
                if (this.editingTitle_value.trim().length === 0) {
                    toastr.error("Your title is empty!");
                    return;
                }
                try {
                    this.editingTitle_saving = true;
                    const t = await ForumService.updateThread(
                        this.thread.id,
                        { title: this.editingTitle_value },
                    );
                    this.thread.title = t.title;
                    this.updateWindowTitle();
                    this.editingTitle = false;
                } finally {
                    this.editingTitle_saving = false;
                }
            },

            async saveCategory() {
                if (this.editingCategory_saving) {
                    return;
                }
                try {
                    this.editingCategory_saving = true;
                    const t = await ForumService.updateThread(
                        this.thread.id,
                        { categoryId: this.editingCategory_value },
                    );
                    this.thread.category = t.category;
                    this.editingCategory = false;
                } finally {
                    this.editingCategory_saving = false;
                }
            },

            async toggleSticky() {
                try {
                    this.editingSticky_saving = true;
                    const t = await ForumService.updateThread(
                        this.thread.id,
                        { sticky: !this.thread.sticky },
                    );
                    this.thread.sticky = t.sticky;
                } finally {
                    this.editingSticky_saving = false;
                }
            },

            initThreadReply() {
                if (!this.threadReply) {
                    this.threadReply = {
                        content: '',
                        author: this.currentAuthor,
                    };
                }
            },

            cancelThreadReply() {
                this.threadReply = null;
            },

            async uploadContentFiles(content, contentFiles) {
                try {
                    return ForumUtils.uploadContentFiles(content, contentFiles);
                } catch (err) {
                    console.log(
                        'Error uploading content files:',
                        err,
                        content,
                        contentFiles,
                    );
                    toastr.error(`
                        There was an error while uploading the post's files.
                        Please try again later.
                    `);
                    throw err;
                }
            },

            async saveThreadReply(fields) {
                if (!this.threadReply) {
                    return;
                }
                if (fields.content.trim().length === 0) {
                    toastr.error("Your post is empty! Try adding some cssontent before posting.");
                    return;
                }
                fields.content = await this.uploadContentFiles(
                    fields.content,
                    fields.contentFiles,
                );

                const post = await this.threadWrapper.createPost(fields);
                this.threadReply = null;
                this.$refs.postList.flashPost(post.id);
            },

            async createPost(fields) {
                if (fields.content.trim().length === 0) {
                    toastr.error("Your post is empty! Try adding some content before posting.");
                    return;
                }
                fields.content = await this.uploadContentFiles(
                    fields.content,
                    fields.contentFiles,
                );
                const post = await this.threadWrapper.createPost(fields);
                this.$refs.postList.closeReplyToPost(fields.sourcePostId);
                this.$refs.postList.flashPost(post.id);
            },

            async updatePost(post) {
                if (post.content.trim().length === 0) {
                    toastr.error("Your post is empty! Try adding some content before posting.");
                    return;
                }
                post.content = await this.uploadContentFiles(
                    post.content,
                    post.contentFiles,
                );
                await this.threadWrapper.updatePost(post);
                this.$refs.postList.stopEditingPost(post.id);
            },

            async likePost(postId) {
                await this.threadWrapper.likePost(postId);
            },

            async unlikePost(postId) {
                await this.threadWrapper.unlikePost(postId);
            },

            async deletePost(postId) {
                await this.threadWrapper.deletePost(postId);
            },

            async updateThread(fields) {
                // Clear paths if no non-foundation terms are selected.
                const hasNonFoundation = fields.termTypes.find(tt => {
                    return tt !== 'foundation';
                });
                if (!hasNonFoundation) {
                    fields.pathIds = [];
                }

                await this.threadWrapper.updateThread(fields);
                this.$refs.postList.stopEditingSummary();
            },

            async solveThread(postId) {
                await this.threadWrapper.solveThread(postId);
            },

            async unsolveThread() {
                await this.threadWrapper.unsolveThread();
            },

            toggleLinkShare() {
                this.linkShareShowing = !this.linkShareShowing;
                if (this.linkShareShowing) {
                    this.$nextTick(() => {
                        this.$refs.linkShare.highlight();
                    });
                }
            },

            closeLinkShare() {
                this.linkShareShowing = false;
            },

            updateWindowTitle() {
                document.title = `${this.thread.title} | Think Tank`;
            },
        },

        // TODO: move this to just below data()
        computed: {
            cachedUserId() {
                return parseInt(localStorage.user_id);
            },

            cachedUserRoles() {
                return JSON.parse(localStorage.roles);
            },

            currentUser() {
                return this.$store.getters.currentUser;
            },

            currentAuthor() {
                const fName = this.currentUser.firstName;
                const lName = this.currentUser.lastName;
                return {
                    id: this.cachedUserId,
                    name: `${fName} ${lName}`,
                    roles: this.cachedUserRoles,
                    avatarUrl: this.currentUser.avatar,
                    currentTerm: this.currentUser.currentTerm,
                };
            },

            canEditThread() {
                return this.threadWrapper.canEditThread();
            },

            canStickThread() {
                return this.threadWrapper.canStickThread();
            },

            threadCategory() {
                if (!this.thread) {
                    return null;
                }
                return this.thread.category;
            },

            threadCategoryUrl() {
                return {
                    name: 'forum-category',
                    params: {
                        categoryId: this.threadCategory.id,
                        categorySlug: this.threadCategory.name,
                    },
                };
            },

            seeMoreUrl() {
                return this.threadCategoryUrl;
            },

            categories() {
                return this.$store.getters.forumCategories;
            },

            categoryOptions() {
                return this.categories.map(cat => {
                    return {
                        value: cat.id,
                        label: cat.label,
                    };
                });
            },

            /**
             * As the user scrolls down the page, we want to automatically
             * adjust the timeline value; ie. the scroll position determines the
             * timeline value. However, when the timeline slider is being
             * used/dragged, the opposite should happen: the timeline value
             * should determine the scroll position. This flag allows us to
             * enable/disable the scroll position => timeline value feature.
             */
            timelineValue_syncToScroll() {
                return !this.timeline_thumbHeld;
            },

            thread() {
                return this.threadWrapper.thread;
            },

            timeline() {
                return this.threadWrapper.timeline;
            },

            postStore() {
                return this.threadWrapper.postStore;
            },

            timelinePosts() {
                return this.timeline ? this.timeline.posts : [];
            },

            threadUrl() {
                if (this.thread) {
                    const slug = ForumUtils.formatTitlePreview(this.thread.title);
                    const res = this.$router.resolve({
                        name: 'forum-thread',
                        params: {
                            threadId: this.thread.id,
                            threadTitlePreview: slug,
                        },
                    });
                    return `${window.location.origin}/${res.href}`;
                } else {
                    return null;
                }
            },

            homeUrl() {
                return {
                    name: 'forum-home',
                };
            },

            searchUrl() {
                return {
                    name: 'forum-search',
                };
            },

            showCurrentPostWindow() {
                return (!this.loading_initial &&
                        this.currentPostWindow.length > 0 &&
                        this.currentPostWindow.isLoaded());
            },

            showLoader() {
                const initial = this.loading_initial;
                const cpwEmpty = (this.currentPostWindow.length === 0 ||
                                  !this.currentPostWindow.isLoaded());
                return initial || cpwEmpty;
            },

            showLoaderBefore() {
                return (this.currentPostWindow.length > 0 &&
                        this.currentPostWindow.isLoaded() &&
                        this.currentPostWindow.hasPostsBefore());
            },

            showLoaderAfter() {
                return (this.currentPostWindow.length > 0 &&
                        this.currentPostWindow.isLoaded() &&
                        this.currentPostWindow.hasPostsAfter());
            },

            showRelatedThreads() {
                return this.relatedThreads.length > 0;
            },

            threadReplyBtnClasses() {
                return {
                    btn: true,
                    'btn-default': this.threadReply,
                    'btn-success': !this.threadReply,
                };
            },
        },

        watch: {
            'postStore.loadedPosts': function() {
                // We need to make sure that when new posts are loaded (either
                // by scrolling or from the user creating a new post) we expand
                // our window to potentially include them.
                const index = this.timelineValueToIndex(this.timelineValue);
                const postWindow = this.postStore.getLoadedWindowAt(index);
                this.currentPostWindow = postWindow;


            },

            currentPostWindow: function() {
                // When the current post window changes, the height of the
                // document may change, pushing the timeline out of it's usual
                // boundaries.
                if (this.$refs.timeline) {
                    this.$refs.timeline.updateStickyMode();
                }
            },
        },
    }
</script>

<style lang="scss" scoped>
    .flex {
        display: flex;
    }
    .flex-full {
        flex: 1;
    }
    .flex-none {
        flex: 0;
    }

    .panel .panel-body {
        margin: 20px 0;
        padding: 40px;
    }

    .action-bar {
        line-height: 40px;

        a {
            display: block;
            font-size: 24px;
            color: #b0b0b0;
            width: 40px;
            text-align: center;

            &:hover {
                background: #363636;
            }

            i {
                position: relative;
                top: -2px;
            }
        }
    }

    .input-wrapper {
        position: relative;

        input {
            padding: 0 35px 0 5px;
            line-height: 35px;
            height: 35px;
            border-color: #b0b0b0 !important;
            &:focus {
                border-color: #fff !important;
            }
        }

        button {
            background: none;
            border: none;
            position: absolute;
            top: 0;
            right: 0;
            width: 35px;
            height: 35px;

            &:hover {
                background: #363636;
            }

            i {
                font-size: 18px;
            }
        }
    }

    .thread-title {
        margin: 0 0 10px;

        h1 {
            display: flex;
            align-items: center;
            font-size: 39px;
            margin: 0;

            button {
                background: none;
                border: none;
                color: #666;
                padding: 0;
                width: 30px;
                height: 30px;
                &:first-of-type {
                    margin-left: 5px;
                }
                &:hover {
                    background-color: #333;
                    color: #b0b0b0;
                }
                &.accent-sticky {
                    color: rgb(252, 206, 0);
                }

                i {
                    display: block;
                    font-size: 18px;
                }
            }
        }
    }

    .thread-category {
        margin: 0 0 30px;

        > div {
            display: flex;
            align-items: center;
        }

        a {
            color: #33ccff;
        }

        button {
            background: none;
            border: none;
            color: #666;
            padding: 0;
            width: 30px;
            height: 30px;
            margin-left: 5px;
            &:hover {
                background-color: #333;
                i {
                    color: #b0b0b0;
                }
            }

            i {
                display: block;
                font-size: 18px;
            }
        }

        .breadcrumb-divider {
            margin: 0 7px;
        }
    }

    .discussion {
        display: flex;
        position: relative;

        .posts-column {
            width: 100%;
            max-width: 780px;

            .forum-loader {
                border-top: 1px solid #333;
            }
        }

        .pre-timeline-spacer {
            max-width: 40px;
        }

        .forum-thread-timeline {
            margin-left: 40px;
        }
    }

    .thread-reply {
        display: flex;

        .forum-post {
            margin: 0;
            flex: 1;
            max-width: 720px;
            padding: 0 0 20px;
            border-bottom: 1px solid #333;
        }

        .post-reply-spacer {
            // Simulate timeline width
            width: 80px;
            margin-left: 40px;
        }
    }

    .action-btns {
        margin: 30px 0;

        .btn {
            padding: 8px 10px;
            margin-right: 8px;

            &.accent-hover {
                background: black;
                border-color: #33ccff;
            }

            i {
                margin-right: 3px;
            }
        }
    }

    .link-share-wrapper {
        position: relative;
        display: inline-block;

        .forum-share {
            position: absolute;
            top: -50px; // height of forum share + 6px padding
            left: 0;
            width: 300px;
        }
    }

    h2 {
        font-size: 24px;
        margin: 60px 0 10px;
    }

    .see-more-link {
        padding: 15px 10px;
        border-bottom: 1px solid #333;
    }
</style>



// WEBPACK FOOTER //
// ForumThread.vue?83a3eab4